/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "nesting" }] */
import { FormEvent, useContext, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  AffiliateFillRecord,
  AffiliateState,
} from "../../interfaces/affiliate";
import {
  AffiliateHistoryScope,
  useAffiliateHistory,
} from "../../hooks/api/admin/useAffiliateHistory";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import { verifiedAffiliateEnvironment } from "../../hooks/api/admin/affiliateEnvironment";

const date = (value: string) => new Date(value).toLocaleString();

function FillTable({ fills }: { fills: AffiliateFillRecord[] }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table>
        <thead>
          <tr>
            {[
              "Fill / time",
              "Wallet",
              "Fee paid",
              "Fee waived",
              "Cash commission",
              "DAO AEVO value",
              "Earned points",
              "Calculation",
            ].map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fills.map((fill) => (
            <tr key={`${fill.request.wallet}:${fill.request.fill_id}`}>
              <td>
                {fill.request.fill_id}
                <br />
                {date(fill.request.at)}
              </td>
              <td>
                <code>{fill.request.wallet}</code>
              </td>
              <td>${fill.fee_paid}</td>
              <td>${fill.fee_waived}</td>
              <td>${fill.accrual.cash_usd}</td>
              <td>${fill.accrual.dao_aevo_usd}</td>
              <td>{fill.accrual.earned_points}</td>
              <td>
                <details>
                  <summary>Inspect fill</summary>
                  <pre>{JSON.stringify(fill, null, 2)}</pre>
                </details>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PersistedHistory({
  scope,
  address,
  generation,
}: {
  scope: AffiliateHistoryScope;
  address: string;
  generation: number;
}) {
  const [cursors, setCursors] = useState([""]);
  const cursor = cursors[cursors.length - 1];
  const history = useAffiliateHistory(true, scope, address, generation, cursor);
  const loading = !history.data && !history.error;
  return (
    <>
      <p>
        History for <code>{address}</code> · Page {cursors.length}
      </p>
      {loading && <p role="status">Loading recorded trade history…</p>}
      {history.error && (
        <div role="alert">
          Recorded trade history unavailable: {affiliateError(history.error)}
        </div>
      )}
      {!history.error &&
        history.data &&
        (history.data.fills.length ? (
          <>
            <p>{history.data.fills.length} records on this page.</p>
            <FillTable fills={history.data.fills} />
          </>
        ) : (
          <p>
            No recorded affiliate fills for this selection in the current
            program.
          </p>
        ))}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          disabled={cursors.length === 1 || history.isValidating}
          onClick={() => setCursors((previous) => previous.slice(0, -1))}
        >
          Previous page
        </button>
        <button
          type="button"
          disabled={
            !history.data?.next_cursor ||
            Boolean(history.error) ||
            history.isValidating
          }
          onClick={() => {
            if (history.data?.next_cursor)
              setCursors((previous) => [
                ...previous,
                history.data!.next_cursor,
              ]);
          }}
        >
          Next page
        </button>
        <button
          type="button"
          disabled={history.isValidating}
          onClick={() => history.mutate()}
        >
          Refresh history
        </button>
      </div>
    </>
  );
}

function CompactHistory({ state }: { state: AffiliateState }) {
  const [scope, setScope] = useState<AffiliateHistoryScope>("affiliate");
  const [address, setAddress] = useState(state.affiliates[0]?.address || "");
  const [selection, setSelection] = useState<{
    scope: AffiliateHistoryScope;
    address: string;
  }>();
  const [error, setError] = useState<string>();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = address.trim().toLowerCase();
    if (!/^0x[0-9a-f]{40}$/.test(value)) {
      setError("Enter a valid affiliate or trader wallet address.");
      return;
    }
    setError(undefined);
    setSelection({ scope, address: value });
  };
  return (
    <>
      <section>
        <h2>Recorded trading fees and affiliate earnings</h2>
        <p>Find recorded fees and rewards for an affiliate or trader.</p>
        <form onSubmit={submit}>
          <label>
            History scope
            <select
              value={scope}
              onChange={(event) =>
                setScope(event.target.value as AffiliateHistoryScope)
              }
            >
              <option value="affiliate">Affiliate</option>
              <option value="wallet">Trader wallet</option>
            </select>
          </label>
          <label>
            History wallet address
            <input
              required
              pattern="0x[a-fA-F0-9]{40}"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              autoComplete="off"
              style={{ width: 340 }}
            />
          </label>
          <button type="submit">Load recorded history</button>
        </form>
        {error && <p role="alert">{error}</p>}
        {selection ? (
          <PersistedHistory
            key={`${selection.scope}:${selection.address}:${
              state.reset_generation || 0
            }`}
            scope={selection.scope}
            address={selection.address}
            generation={state.reset_generation || 0}
          />
        ) : (
          <p>Choose an affiliate or trader to load its recorded fills.</p>
        )}
      </section>
      <section>
        <h2>Pending payout obligations</h2>
        <p>
          Unpaid commissions, including held and reserved amounts. Not lifetime
          earnings.
        </p>
        {state.ledger.fills.length ? (
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  {[
                    "Fill / time",
                    "Trader",
                    "Affiliate",
                    "Pending cash",
                    "Pending DAO value",
                  ].map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.ledger.fills.map((fill) => (
                  <tr key={`${fill.request.wallet}:${fill.request.fill_id}`}>
                    <td>
                      {fill.request.fill_id}
                      <br />
                      {date(fill.request.at)}
                    </td>
                    <td>
                      <code>{fill.request.wallet}</code>
                    </td>
                    <td>
                      <code>{fill.request.affiliate_id}</code>
                    </td>
                    <td>${fill.accrual.cash_usd}</td>
                    <td>${fill.accrual.dao_aevo_usd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>
            No pending payout obligations. This does not mean there is no
            trading history.
          </p>
        )}
      </section>
    </>
  );
}

export function TradeHistoryPanel({ state }: { state?: AffiliateState }) {
  const { tokenId } = useContext(AuthContext);
  if (!state || !verifiedAffiliateEnvironment(state) || !tokenId)
    return (
      <section>
        <p>
          Sign in and load verified affiliate state to inspect trade history.
        </p>
      </section>
    );
  if (state.compact_state)
    return <CompactHistory key={tokenId} state={state} />;
  return (
    <section>
      <h2>Recorded trading fees and affiliate earnings</h2>
      <p>Recorded trading fees and rewards.</p>
      {state.ledger.fills.length ? (
        <FillTable fills={state.ledger.fills} />
      ) : (
        <p>No affiliate fills have been recorded.</p>
      )}
    </section>
  );
}
